import { useField } from 'formik'
import Styles from './GeneratedDesignPreview.module.scss'
import { GeneratedDesignPreviewProps } from './Types'
import { forwardRef, useEffect, useMemo } from 'react'
import { useGenerateDesignContentSync } from 'hooks/queries/workspaces/useGenerateDesignContentSync'
import { LoadingShimmerer } from 'components/LoadingShimmerer/LoadingShimmerer'
import { DesignContentPreview } from 'components/DesignContentPreview/DesignContentPreview'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { Ref } from 'components/DesignContentPreview/Types'

const GeneratedDesignPreview = observer(
  forwardRef<Ref, GeneratedDesignPreviewProps>(({ templateId, initialMediaFileId, onChange, onGenerate }, ref) => {
    const { visualElements } = useRootStore()
    const [{ value: topic }] = useField('topic')
    const [{ value: language }] = useField('language')
    const [{ value: brand_voice_id }] = useField('brand_voice_id')
    const [{ value: tone }] = useField('tone')
    const [{ value: brand_kit_id }] = useField('brand_kit_id')
    const [{ value: brand_visual_kit_id }] = useField('brand_visual_kit_id')
    const generateMutation = useGenerateDesignContentSync({
      retry: 3,
      onError: () => onGenerate?.('failed'),
      onSuccess: () => onGenerate?.('successful'),
    })
    const toneDep = JSON.stringify(tone)

    const template = visualElements.getElement(templateId)

    useEffect(() => {
      generateMutation.mutate({
        source_visual_element_id: templateId,
        topic,
        language,
        brand_voice_id,
        tone,
        brand_kit_id,
        brand_visual_kit_id,
        apply_brand: true,
        initial_media_file_id: initialMediaFileId,
      })
    }, [templateId, topic, language, brand_voice_id, toneDep, brand_kit_id, brand_visual_kit_id]) // eslint-disable-line react-hooks/exhaustive-deps

    const request = generateMutation.data
    const generatedContent = request?.generated_content

    const content = useMemo(() => {
      if (generatedContent) {
        return JSON.parse(generatedContent)
      }
    }, [generatedContent])

    useEffect(() => {
      onChange(content)
    }, [content]) // eslint-disable-line react-hooks/exhaustive-deps

    if (!template) {
      return null
    }

    return (
      <div className={Styles.root}>
        <LoadingShimmerer
          className={Styles.designPreviewContainer}
          show={generateMutation.isLoading}
          borderRadius={5}
          style={{ width: '100%', aspectRatio: template.width / template.height }}
        >
          {content && (
            <div className={Styles.designPreview} data-testid={`design-content-preview-${templateId}`}>
              <DesignContentPreview content={content} ref={ref} />
            </div>
          )}
          {!content && generateMutation.error && (
            <div className={Styles.designPreview}>
              <div className={Styles.error} />
            </div>
          )}
        </LoadingShimmerer>
      </div>
    )
  }),
)

export { GeneratedDesignPreview }
